import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const StyledBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(2, 12, 27, 0.78);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  padding: 24px;
`;

const StyledDialog = styled.div`
  ${({ theme }) => theme.mixins.boxShadow};
  width: min(860px, 100%);
  max-height: min(82vh, 820px);
  overflow: auto;
  background: var(--light-navy);
  border-radius: var(--border-radius);
  border: 1px solid var(--lightest-navy);

  header {
    ${({ theme }) => theme.mixins.flexBetween};
    gap: 16px;
    padding: 18px 18px 10px;
    position: sticky;
    top: 0;
    background: linear-gradient(to bottom, rgba(10, 25, 47, 0.95), rgba(10, 25, 47, 0.75));
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(136, 146, 176, 0.18);
  }

  h3 {
    margin: 0;
    color: var(--lightest-slate);
    font-size: clamp(18px, 3vw, 22px);
    line-height: 1.25;
  }

  .close {
    ${({ theme }) => theme.mixins.smallButton};
    padding: 10px 12px;
    background-color: transparent;
    color: var(--green);
  }

  .body {
    padding: 12px 18px 18px;
    color: var(--light-slate);
    font-size: 17px;
    line-height: 1.7;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    p {
      margin: 0 0 12px;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }
`;

const Modal = ({ isOpen, title, onClose, children, ariaLabel }) => {
  const dialogRef = useRef(null);
  const lastActiveRef = useRef(null);

  const portalTarget = useMemo(() => {
    if (typeof document === 'undefined') {return null;}
    return document.body;
  }, []);

  useEffect(() => {
    if (!isOpen) {return;}
    if (typeof document === 'undefined') {return;}

    lastActiveRef.current = document.activeElement;
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    // Slight delay to ensure it's mounted before focusing
    setTimeout(() => dialogRef.current?.focus?.(), 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      lastActiveRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen || !portalTarget) {
    return null;
  }

  return createPortal(
    <StyledBackdrop
      role="presentation"
      onMouseDown={e => {
        if (e.target === e.currentTarget) {onClose?.();}
      }}>
      <StyledDialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Dialog'}
        tabIndex={-1}>
        <header>
          <h3>{title}</h3>
          <button className="close" type="button" onClick={onClose} aria-label="Close dialog">
            Close
          </button>
        </header>
        <div className="body">{children}</div>
      </StyledDialog>
    </StyledBackdrop>,
    portalTarget,
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default Modal;
